import React, { ReactNode, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Col, Row, Steps } from 'antd'
import classNames from 'classnames'

import { StepStatus as ItemStatus, WizardStep } from 'core/domain/wizard/wizard'

import { ROUTE_PATHS } from 'common/routePaths'

import { getNavigationItemFirstChild, getNavigationItemFullPath } from './helpers'

const { ICHRA_DECISION_TOOL_PATH } = ROUTE_PATHS

const { Step } = Steps

export const DEFAULT_CATEGORY_INDEX = 0
export const DEFAULT_EDITING_CATEGORY_INDEX = -1
export const DEFAULT_CATEGORY_ITEM_INDEX = 0
export const DEFAULT_EDITING_CATEGORY_ITEM_INDEX = -1

declare type StepStatus = 'finish' | 'process' | 'wait'

interface SignUpNavigationProps {
  state: WizardStep[]
  activeItem?: ActiveItemProps
  redirectTo: (path: string) => void
}

export interface ActiveItemProps {
  categoryIndex: number
  categoryItemIndex: number
}

const SignUpNavigationMenu: React.FC<SignUpNavigationProps> = (props) => {
  const [editingWizardStep, setEditingWizardStep] = useState({
    categoryIndex: props.activeItem ? props.activeItem.categoryIndex : DEFAULT_EDITING_CATEGORY_INDEX,
    categoryItemIndex: props.activeItem ? props.activeItem.categoryItemIndex : DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
  })
  const [activeCollapseKey, setActiveCollapseKey] = useState<string | string[]>()
  const history = useHistory()

  useEffect(() => {
    setEditingWizardStep({
      categoryIndex: props.activeItem ? props.activeItem.categoryIndex : DEFAULT_EDITING_CATEGORY_INDEX,
      categoryItemIndex: props.activeItem ? props.activeItem.categoryItemIndex : DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
    })
    if (props.activeItem !== undefined && activeCollapseKey !== props.activeItem.categoryIndex.toString())
      setActiveCollapseKey(props.activeItem.categoryIndex.toString())
  }, [props.activeItem])

  const categoriesGenerator = (state: WizardStep[]): ReactNode => {
    if (state === undefined || state.length === 0) return null

    return categoryItemsGenerator(state)
  }

  const getIndexOfActiveCategory = (state: WizardStep[]): number => {
    try {
      if (editingWizardStep.categoryIndex !== DEFAULT_EDITING_CATEGORY_INDEX) {
        const category = state.find((cat) => cat.priority === editingWizardStep.categoryIndex)
        if (category === undefined) throw new Error('Category not found')
        const categoryStatus = getCategoryStatus(category)
        if (categoryStatus === ItemStatus.Active || categoryStatus === ItemStatus.Complete) {
          return editingWizardStep.categoryIndex
        } else {
          setEditingWizardStep({
            categoryIndex: DEFAULT_EDITING_CATEGORY_INDEX,
            categoryItemIndex: DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
          })
        }
      }
      const category = state.find((category) => getCategoryStatus(category) === ItemStatus.Active)
      return category ? category.priority : DEFAULT_CATEGORY_INDEX
    } catch (e) {
      console.log(e)
      return DEFAULT_CATEGORY_INDEX
    }
  }

  const getCategoryStatus = (category: WizardStep): ItemStatus => {
    if (
      editingWizardStep.categoryIndex === category.priority &&
      (category.children.some((categoryItem) => categoryItem.status === ItemStatus.Active) ||
        category.children.every((categoryItem) => categoryItem.status === ItemStatus.Complete))
    ) {
      return ItemStatus.Active
    } else {
      return category.status
    }
  }

  const categoryItemsGenerator = (state: WizardStep[]): ReactNode => {
    const activeKey = getIndexOfActiveCategory(state)

    return (
      <Steps current={activeKey} size="small" className="site-navigation-steps" type="navigation">
        {state
          .sort((first, second) => first.priority - second.priority)
          .map((category, index) => {
            const categoryStatus = getCategoryStatus(category)
            const activeCategoryItem = getIndexOfActiveCategoryItem(category)
            return (
              <Step
                title={getTitleContentOfCategoryItem(category)}
                // key={0}
                // status={categoryItemStatus}
                className={classNames({
                  steps__item: true,
                  steps__item_active:
                    ((categoryStatus === ItemStatus.Active &&
                      editingWizardStep.categoryIndex === DEFAULT_EDITING_CATEGORY_INDEX) ||
                      editingWizardStep.categoryIndex === category.priority) &&
                    activeCategoryItem === index,
                })}
              />
            )
          })}
      </Steps>
    )
  }

  const getIndexOfActiveCategoryItem = (category: WizardStep): number => {
    try {
      if (
        editingWizardStep.categoryIndex !== DEFAULT_EDITING_CATEGORY_INDEX &&
        editingWizardStep.categoryIndex === category.priority &&
        editingWizardStep.categoryItemIndex !== DEFAULT_EDITING_CATEGORY_ITEM_INDEX
      ) {
        const categoryItem = category.children.find((item) => item.priority === editingWizardStep.categoryItemIndex)
        if (categoryItem === undefined) throw new Error('Category Item not found')
        if (categoryItem.status === ItemStatus.Active || categoryItem.status === ItemStatus.Complete) {
          return editingWizardStep.categoryItemIndex
        } else {
          setEditingWizardStep({
            categoryIndex: DEFAULT_EDITING_CATEGORY_INDEX,
            categoryItemIndex: DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
          })
        }
      } else if (
        editingWizardStep.categoryIndex !== DEFAULT_EDITING_CATEGORY_INDEX &&
        editingWizardStep.categoryItemIndex !== DEFAULT_EDITING_CATEGORY_ITEM_INDEX
      ) {
        return DEFAULT_CATEGORY_ITEM_INDEX
      }

      let activeCategoryItem = category.children.find((categoryItem) => categoryItem.status === ItemStatus.Active)

      if (activeCategoryItem === undefined) return DEFAULT_CATEGORY_ITEM_INDEX

      redirectTo(activeCategoryItem)
      return activeCategoryItem.priority
    } catch (e) {
      console.log(category)
      console.log(e)
      return DEFAULT_CATEGORY_ITEM_INDEX
    }
  }

  const redirectTo = (categoryItem: WizardStep) => {
    const categoryItemPath = `${ICHRA_DECISION_TOOL_PATH}/${getNavigationItemFullPath(categoryItem)}`

    if (history.location.pathname !== categoryItemPath) history.push(categoryItemPath)
  }

  const getCategoryItemStatus = (category: WizardStep, categoryItem: WizardStep): StepStatus => {
    if (
      editingWizardStep.categoryIndex === category.priority &&
      editingWizardStep.categoryItemIndex === categoryItem.priority
    ) {
      return 'process'
    } else if (editingWizardStep.categoryItemIndex >= 0 && categoryItem.status === ItemStatus.Active) {
      return 'wait'
    } else if (categoryItem.status === ItemStatus.Complete) {
      return 'finish'
    } else if (categoryItem.status === ItemStatus.Active) {
      return 'process'
    } else {
      return 'wait'
    }
  }

  const getTitleContentOfCategoryItem = (categoryItem: WizardStep): ReactNode => {
    return categoryItem.status === ItemStatus.Active || categoryItem.status === ItemStatus.Complete ? (
      <label
        className="pointer"
        onClick={() => {
          const categoryItemPath = getNavigationItemFirstChild(categoryItem)
          props.redirectTo(categoryItemPath)
        }}
      >
        {categoryItem.name}
      </label>
    ) : (
      categoryItem.name
    )
  }

  return (
    <section className="">
      <Row className="pt-24">
        <Col>{categoriesGenerator(props.state)}</Col>
      </Row>
    </section>
  )
}

export default SignUpNavigationMenu
